import React, { useEffect, useState } from "react";
import LiveFeed from "./LiveFeed";
import "../../styles/sidebar/TargetBank.css";
import { useMamdani } from "../../context/MamdaniContext";

const API       = "https://api.anti-bot.ai";
const API_FETCH = "https://api.anti-bot.ai";

export default function TargetBank() {
  const { mamdaniMode: mm, data: mmData } = useMamdani();
  const [politicians, setPoliticians] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [searching, setSearching]     = useState(false);

  const [searchInput, setSearchInput]     = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser]   = useState(null);

  const [activeUsername, setActiveUsername] = useState(null);
  const [error, setError]                   = useState("");
  const [successMsg, setSuccessMsg]         = useState("");

  useEffect(() => { if (!mm) fetchTargets(); }, [mm]);

  const fetchTargets = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/politicians`);
      const data = await res.json();
      if (data.success) setPoliticians(data.politicians);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSearch = async () => {
    const name = searchInput.trim();
    if (!name) { setError("נא להזין שם לחיפוש"); return; }
    setSearching(true); setError(""); setSearchResults([]); setSelectedUser(null);
    try {
      const res  = await fetch(`${API_FETCH}/api/targets/search?name=${encodeURIComponent(name)}`);
      const data = await res.json();
      if (data.success && data.results.length > 0) setSearchResults(data.results);
      else setError("לא נמצאו תוצאות — נסה שם אחר");
    } catch (e) { setError("❌ שגיאה בחיבור לשרת"); }
    finally { setSearching(false); }
  };

  const handleAdd = () => {
    if (!selectedUser) { setError("נא לבחור חשבון"); return; }
    setError(""); setSuccessMsg("");
    setActiveUsername(selectedUser.username);
  };

  const handleFeedDone = (data) => {
    setSuccessMsg(`✅ @${data?.username} נוסף! ${data?.replies} תגובות, ${data?.bots} בוטים`);
    setSearchInput(""); setSearchResults([]); setSelectedUser(null);
    fetchTargets();
    setTimeout(() => setActiveUsername(null), 8000);
  };

  const handleRemove = async (username) => {
    if (!window.confirm(`למחוק את @${username}?`)) return;
    try {
      const res  = await fetch(`${API_FETCH}/api/targets/remove/${username}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) { setSuccessMsg(`🗑️ @${username} הוסר`); fetchTargets(); }
    } catch (e) { setError("❌ שגיאה בחיבור לשרת"); }
  };

  const accounts      = mm ? mmData.accounts : politicians;
  const totalBots     = mm
    ? mmData.allBots.length
    : politicians.reduce((s, p) => s + (p.bot_count || 0), 0);
  const getBotCount   = (p) => mm ? (p.bots?.length ?? 0) : (p.bot_count || 0);
  const protectedCount = accounts.filter(p => getBotCount(p) === 0).length;

  return (
    <div className="target-bank-page" dir={mm ? "ltr" : "rtl"}>

      <div className="tb-header">
        <span className="tb-title">{mm ? "Target Bank" : "בנק מטרות"}</span>
      </div>

      <div className="tb-stats">
        <div className="tb-stat-card">
          <div className="tb-stat-value red">{totalBots}</div>
          <div className="tb-stat-label">{mm ? "Bots" : "התקפות"}</div>
        </div>
        <div className="tb-stat-card">
          <div className="tb-stat-value green">{protectedCount}</div>
          <div className="tb-stat-label">{mm ? "Clean" : "מוגנים"}</div>
        </div>
        <div className="tb-stat-card">
          <div className="tb-stat-value blue">{accounts.length}</div>
          <div className="tb-stat-label">{mm ? "Monitored" : "במעקב"}</div>
        </div>
        <div className="tb-stat-card">
          <div className="tb-stat-value orange">{accounts.filter(p => getBotCount(p) > 50).length}</div>
          <div className="tb-stat-label">{mm ? "Critical" : "קריטי"}</div>
        </div>
      </div>

      {!mm && (
        <div className="tb-add-section">
          <div className="tb-add-title">הוסף חשבון חדש למעקב</div>

          <div className="tb-add-row">
            <button className="tb-btn-search" onClick={handleSearch}
              disabled={searching || !!activeUsername}>
              {searching ? "מחפש..." : "חפש"}
            </button>
            <input
              className="tb-input"
              placeholder="חפש לפי שם — לדוגמה: יאיר לפיד"
              value={searchInput}
              onChange={e => { setSearchInput(e.target.value); setError(""); setSearchResults([]); setSelectedUser(null); }}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              disabled={searching || !!activeUsername}
            />
          </div>

          {searchResults.length > 0 && !activeUsername && (
            <div className="tb-search-results">
              <div className="tb-search-label">בחר חשבון:</div>
              {searchResults.map((u, i) => (
                <div key={i}
                  className={`tb-search-item ${selectedUser?.username === u.username ? "selected" : ""}`}
                  onClick={() => setSelectedUser(u)}>
                  {u.image && <img src={u.image} alt={u.username} className="tb-avatar" />}
                  <div className="tb-search-info">
                    <span className="tb-search-name">{u.name}</span>
                    <span className="tb-search-username">@{u.username}</span>
                  </div>
                  {selectedUser?.username === u.username && <span className="tb-check">✓</span>}
                </div>
              ))}
              {selectedUser && (
                <button className="tb-btn-add" onClick={handleAdd}>
                  הוסף את @{selectedUser.username} למעקב
                </button>
              )}
            </div>
          )}

          {activeUsername && (
            <LiveFeed username={activeUsername} onDone={handleFeedDone} />
          )}

          {error      && <div className="tb-msg error">{error}</div>}
          {successMsg && <div className="tb-msg success">{successMsg}</div>}
        </div>
      )}

      <div className="tb-section">
        <div className="tb-section-header">
          <span className="tb-section-title">
            {mm ? `Watchlist Accounts (${accounts.length})` : `חשבונות במעקב (${accounts.length})`}
          </span>
          {!mm && <button className="tb-btn-sm" onClick={fetchTargets}>רענן</button>}
        </div>

        <div className="tb-table-wrap">
          {loading && !mm ? (
            <div className="tb-loading">טוען נתונים...</div>
          ) : accounts.length === 0 ? (
            <div className="tb-loading">{mm ? "No accounts in watchlist" : "אין חשבונות — הוסף את הראשון"}</div>
          ) : (
            <table className="tb-table">
              <thead>
                <tr>
                  <th>{mm ? "Account" : "חשבון"}</th>
                  <th>{mm ? "Name" : "שם"}</th>
                  <th>{mm ? "Bots" : "בוטים"}</th>
                  <th>{mm ? "Status" : "סטטוס"}</th>
                  {!mm && <th>פעולות</th>}
                </tr>
              </thead>
              <tbody>
                {accounts.map((p, i) => {
                  const botCount = getBotCount(p);
                  return (
                    <tr key={i}>
                      <td>
                        <div className="tb-target-name">
                          {(p.profile_image_url || p.profile_image) && (
                            <img src={p.profile_image_url || p.profile_image} alt={p.username} className="tb-avatar" />
                          )}
                          <span>@{p.username}</span>
                          <span className={`tb-dot ${botCount > 0 ? "active" : "inactive"}`} />
                        </div>
                      </td>
                      <td style={{ color: "#94a3b8" }}>{p.display_name || p.name || "—"}</td>
                      <td><span className="tb-attack">{botCount}</span></td>
                      <td>
                        <span className={`tb-badge ${botCount > 50 ? "red" : botCount > 20 ? "orange" : botCount > 0 ? "blue" : "green"}`}>
                          {botCount > 50
                            ? (mm ? "Critical" : "קריטי")
                            : botCount > 20
                              ? (mm ? "High" : "גבוה")
                              : botCount > 0
                                ? (mm ? "Active" : "פעיל")
                                : (mm ? "Clean" : "מוגן")}
                        </span>
                      </td>
                      {!mm && (
                        <td>
                          <button className="tb-btn-action del" onClick={() => handleRemove(p.username)}>הסר</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

    </div>
  );
}